import React, { useCallback, useEffect, useState } from 'react';
import { RevenueScheduleService } from '@vanilla/services/RevenueScheduleService.js';
import { RevenueSetupService } from '@vanilla/services/RevenueSetupService.js';
import { REVENUE_SETUP_LABELS } from '@vanilla/constants/RevenueSetup.js';
import { formatCurrency } from '@vanilla/utils/Utils.js';
import { ToastManager } from '@/lib/toast.js';
import { Badge, statusBadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    formatZohoSyncedAgo,
    zohoStatusBadgeVariant,
    zohoStatusDetail,
    zohoStatusLabel,
} from '@vanilla/constants/ZohoInvoiceStatus.js';

function scheduleAmountValue(row) {
    const raw = row?.plannedAmount ?? row?.amount;
    if (raw == null || raw === '') return '';
    const n = Number(raw);
    return Number.isFinite(n) ? String(n) : '';
}

function ScheduleAmountInput({ schedule, disabled, onCommit }) {
    const committed = scheduleAmountValue(schedule);
    const [draft, setDraft] = useState(committed);
    useEffect(() => {
        setDraft(committed);
    }, [committed, schedule?.id]);

    return (
        <Input
            unstyled
            type="text"
            inputMode="decimal"
            className="form-input schedule-amount-edit"
            style={{ width: 120 }}
            disabled={disabled}
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}
            onBlur={() => {
                const n = parseFloat(draft);
                if (Number.isNaN(n) || n < 0) {
                    setDraft(committed);
                    return;
                }
                if (n === Number(committed)) return;
                onCommit(n);
            }}
        />
    );
}

function CancelDialog({ scheduleId, onClose, onConfirm }) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    return (
        <div
            className="quick-create-popup-backdrop active"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
        >
            <div style={{ background: '#fff', borderRadius: 8, padding: 24, minWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                <h3 style={{ margin: '0 0 16px' }}>Cancel Invoice</h3>
                <p style={{ margin: '0 0 12px', color: '#666' }}>Provide a reason for cancellation (optional):</p>
                <Textarea
                    unstyled
                    className="form-input"
                    rows={3}
                    placeholder="Reason for cancellation..."
                    style={{ width: '100%', marginBottom: 16 }}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    autoFocus
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button type="button" variant="secondary" className="btn btn-secondary" onClick={onClose} disabled={busy}>Close</Button>
                    <Button
                        type="button"
                        variant="destructive"
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={async () => {
                            setBusy(true);
                            try {
                                await onConfirm(reason.trim());
                                onClose();
                            } finally {
                                setBusy(false);
                            }
                        }}
                    >
                        Confirm Cancellation
                    </Button>
                </div>
            </div>
        </div>
    );
}

function LinkZohoDialog({ dealId, scheduleId, onClose, onLinked }) {
    const [lookup, setLookup] = useState('');
    const [busy, setBusy] = useState(false);
    return (
        <div
            className="quick-create-popup-backdrop active"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
        >
            <div style={{ background: '#fff', borderRadius: 8, padding: 24, minWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                <h3 style={{ margin: '0 0 16px' }}>Link to Zoho invoice</h3>
                <p style={{ margin: '0 0 12px', color: '#666' }}>
                    Paste a Zoho Books invoice ID or invoice number. CRM will copy status only — it does not create or edit invoices in Zoho.
                </p>
                <Input
                    unstyled
                    className="form-input"
                    placeholder="Invoice ID or INV-000041"
                    style={{ width: '100%', marginBottom: 16 }}
                    value={lookup}
                    onChange={(e) => setLookup(e.target.value)}
                    autoFocus
                    disabled={busy}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button type="button" variant="secondary" className="btn btn-secondary" onClick={onClose} disabled={busy}>Close</Button>
                    <Button
                        type="button"
                        className="btn btn-primary"
                        disabled={busy || !lookup.trim()}
                        onClick={async () => {
                            setBusy(true);
                            try {
                                await RevenueScheduleService.linkZohoInvoice(dealId, scheduleId, lookup.trim());
                                onLinked();
                                onClose();
                            } catch (err) {
                                ToastManager.show({
                                    title: 'Link Zoho invoice',
                                    message: err?.message || 'Could not link Zoho invoice.',
                                    tone: 'error',
                                });
                            } finally {
                                setBusy(false);
                            }
                        }}
                    >
                        Link
                    </Button>
                </div>
            </div>
        </div>
    );
}

/**
 * Revenue setup banner + persisted invoice schedule ops (SoT DealFormManager).
 */
export default function DealRevenueSchedulePanel({ dealId, dealSnapshot }) {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cancelId, setCancelId] = useState(null);
    const [linkId, setLinkId] = useState(null);

    const reload = useCallback(async () => {
        if (!dealId) {
            setSchedules([]);
            return;
        }
        setLoading(true);
        try {
            const rows = await RevenueScheduleService.getByDeal(dealId);
            setSchedules(Array.isArray(rows) ? rows : []);
        } catch (err) {
            console.warn('[DealRevenueSchedulePanel] load failed', err);
            setSchedules([]);
        } finally {
            setLoading(false);
        }
    }, [dealId]);

    useEffect(() => {
        reload();
    }, [reload]);

    const setup = RevenueSetupService.computeStatus(dealSnapshot || {}, schedules);
    const pct = RevenueSetupService.computeCompletionPct(dealSnapshot || {}, schedules);
    const hasRevenueData = (Number(dealSnapshot?.contractValue || dealSnapshot?.dealAmount || 0) > 0)
        || Boolean(dealSnapshot?.paymentModel);
    const showBanner = Boolean(dealId) && hasRevenueData && setup.status !== 'complete';
    const showReady = Boolean(dealId) && setup.status === 'complete';
    const label = REVENUE_SETUP_LABELS[setup.status] || setup.label || setup.status;

    const patchSchedule = async (scheduleId, payload, errMsg) => {
        try {
            await RevenueScheduleService.updateSchedule(dealId, scheduleId, payload);
            await reload();
        } catch (err) {
            ToastManager.show({ title: 'Revenue schedule', message: err?.message || errMsg, tone: 'error' });
        }
    };

    if (!dealId) {
        return (
            <>
                <div id="dealRevenueSetupBanner" className="deal-revenue-setup-banner hidden" role="status" hidden />
                <div id="dealRevenueScheduleHost" className="deal-schedule-host" style={{ marginTop: 16 }}>
                    <p className="field-hint">Save the deal to view revenue schedule.</p>
                </div>
            </>
        );
    }

    return (
        <>
            {Boolean(dealId) && (
                <div
                    id="dealRevenueCompletionPct"
                    className={`deal-revenue-completion${pct === 100 ? ' is-complete' : ''}`}
                    style={{ marginBottom: 8 }}
                >
                    Revenue Completion: {pct}%
                </div>
            )}
            {showReady && (
                <Badge id="dealRevenueReadyBadge" variant="success" className="deal-revenue-ready-badge" role="status">
                    Revenue setup complete
                </Badge>
            )}
            <div
                id="dealRevenueSetupBanner"
                className={`deal-revenue-setup-banner${showBanner ? '' : ' hidden'}`}
                role="status"
                hidden={!showBanner}
            >
                {showBanner && (
                    <>
                        <strong>Revenue setup: {label}</strong>
                        {setup.warnings?.length ? (
                            <ul>{setup.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                        ) : null}
                    </>
                )}
            </div>

            <div id="dealRevenueScheduleHost" className="deal-schedule-host" style={{ marginTop: 16 }}>
                {loading && <p className="field-hint">Loading invoices…</p>}
                {!loading && !schedules.length && (
                    <p className="field-hint">No invoices yet. Save as Closed Won with billing details.</p>
                )}
                {!loading && schedules.length > 0 && (
                    <table className="forecast-table deal-schedule-table">
                        <thead>
                            <tr>
                                <th>Invoice</th>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Zoho</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {schedules.map((s) => {
                                const locked = s.status === 'cancelled' || s.status === 'paid';
                                const zohoDetail = s.zohoInvoiceId ? zohoStatusDetail(s, formatCurrency) : '';
                                const isLinked = Boolean(s.zohoInvoiceId);
                                return (
                                    <tr key={s.id}>
                                        <td>{s.zohoInvoiceId || s.zoho_invoice_id || '—'}</td>
                                        <td>
                                            <Input unstyled
                                                type="text"
                                                className="form-input schedule-date-edit"
                                                style={{ width: 140 }}
                                                disabled={locked}
                                                defaultValue={s.invoiceDate || ''}
                                                onBlur={(e) => {
                                                    const v = e.target.value;
                                                    if (!v || v === (s.invoiceDate || '')) return;
                                                    patchSchedule(s.id, { due_date: v }, 'Could not update invoice date.');
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <ScheduleAmountInput
                                                schedule={s}
                                                disabled={locked}
                                                onCommit={(n) => patchSchedule(s.id, { amount: n }, 'Could not update invoice amount.')}
                                            />
                                        </td>
                                        <td>
                                            {isLinked ? (
                                                <Badge variant={zohoStatusBadgeVariant(s.zohoStatus)} className="status-pill">
                                                    {zohoStatusLabel(s.zohoStatus) || s.zohoStatus || 'Linked'}
                                                </Badge>
                                            ) : (
                                                <Badge variant={statusBadgeVariant(s.status)} className={`status-pill status-${s.status}`}>
                                                    {s.status}{s.cancelReason ? `: ${s.cancelReason}` : ''}
                                                </Badge>
                                            )}
                                        </td>
                                        <td>
                                            {isLinked ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                                    {zohoDetail ? (
                                                        <span className="field-hint" style={{ margin: 0 }}>{zohoDetail}</span>
                                                    ) : null}
                                                    {s.zohoLastSyncedAt ? (
                                                        <span className="field-hint" style={{ margin: 0 }}>
                                                            {formatZohoSyncedAgo(s.zohoLastSyncedAt)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <button type="button" className="link-btn" onClick={() => setLinkId(s.id)}>
                                                    Link to Zoho invoice
                                                </button>
                                            )}
                                        </td>
                                        <td className="crm-row-actions">
                                            {s.zohoInvoiceId ? (
                                                <button
                                                    type="button"
                                                    className="link-btn"
                                                    onClick={async () => {
                                                        if (!window.confirm('Unlink this row from the Zoho invoice? Internal status will be kept.')) {
                                                            return;
                                                        }
                                                        try {
                                                            await RevenueScheduleService.unlinkZohoInvoice(dealId, s.id);
                                                            await reload();
                                                        } catch (err) {
                                                            ToastManager.show({ title: 'Unlink Zoho invoice', message: err?.message || 'Could not unlink invoice.', tone: 'error' });
                                                        }
                                                    }}
                                                >
                                                    Unlink
                                                </button>
                                            ) : (
                                                <>
                                                    {s.status === 'planned' && (
                                                        <button
                                                            type="button"
                                                            className="link-btn"
                                                            onClick={async () => {
                                                                try {
                                                                    await RevenueScheduleService.markIssued(s.id);
                                                                    await reload();
                                                                } catch (err) {
                                                                    ToastManager.show({ title: 'Issue invoice', message: err?.message || 'Could not issue invoice.', tone: 'error' });
                                                                }
                                                            }}
                                                        >
                                                            Issue
                                                        </button>
                                                    )}
                                                    {s.status !== 'paid' && s.status !== 'cancelled' && (
                                                        <button
                                                            type="button"
                                                            className="link-btn"
                                                            onClick={async () => {
                                                                try {
                                                                    await RevenueScheduleService.markPaid(s.id);
                                                                    await reload();
                                                                    window.dispatchEvent(new CustomEvent('dealCommissionScheduleRefresh'));
                                                                } catch (err) {
                                                                    ToastManager.show({ title: 'Mark paid', message: err?.message || 'Could not mark as paid.', tone: 'error' });
                                                                }
                                                            }}
                                                        >
                                                            Mark paid
                                                        </button>
                                                    )}
                                                    {s.status !== 'paid' && s.status !== 'cancelled' && (
                                                        <button type="button" className="link-btn link-danger" onClick={() => setCancelId(s.id)}>
                                                            Cancel
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {!loading && schedules.length > 0 && (
                    <p className="field-hint" style={{ marginTop: 8 }}>
                        Scheduled total: {formatCurrency(schedules.reduce((sum, s) => sum + (Number(s.plannedAmount) || 0), 0))}
                    </p>
                )}
            </div>

            {cancelId && (
                <CancelDialog
                    scheduleId={cancelId}
                    onClose={() => setCancelId(null)}
                    onConfirm={async (reason) => {
                        try {
                            await RevenueScheduleService.cancelSchedule(cancelId, reason);
                            await reload();
                        } catch (err) {
                            ToastManager.show({ title: 'Cancel invoice', message: err?.message || 'Could not cancel invoice.', tone: 'error' });
                            throw err;
                        }
                    }}
                />
            )}
            {linkId && (
                <LinkZohoDialog
                    dealId={dealId}
                    scheduleId={linkId}
                    onClose={() => setLinkId(null)}
                    onLinked={reload}
                />
            )}
        </>
    );
}
